import React, { useState } from 'react';
import { PlusIcon, XIcon, EditIcon, TrashIcon, ClockIcon, FilterIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react';
import { NotificationRule, NotificationEvent, NotificationChannel, NotificationScope } from './NotificationsManagement';
import { StatusBadge } from '../../StatusBadge';
import { OrgUnitSelector } from '../orgunit/OrgUnitSelector';
import type { OrgUnit } from '../../financial/sharedFinancialData';
interface NotificationRulesProps {
  globalRules: NotificationRule[];
  orgUnitRules: NotificationRule[];
  setRules: React.Dispatch<React.SetStateAction<NotificationRule[]>>;
  globalEvents: NotificationEvent[];
  orgUnitEvents: NotificationEvent[];
  setEvents: React.Dispatch<React.SetStateAction<NotificationEvent[]>>;
  channels: NotificationChannel[];
  locale: string;
}
export function NotificationRules({
  globalRules,
  orgUnitRules,
  setRules,
  globalEvents,
  orgUnitEvents,
  setEvents,
  channels,
  locale
}: NotificationRulesProps) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<NotificationRule | null>(null);
  const [selectedEventIds, setSelectedEventIds] = useState<string[]>([]);
  const [selectedChannelIds, setSelectedChannelIds] = useState<string[]>([]);
  const [frequency, setFrequency] = useState<'immediate' | 'daily' | 'weekly'>('immediate');
  const [ruleScope, setRuleScope] = useState<NotificationScope>('Global');
  const [selectedOrgUnit, setSelectedOrgUnit] = useState<OrgUnit | null>(null);
  const allRules = [...globalRules, ...orgUnitRules];
  const allEvents = [...globalEvents, ...orgUnitEvents];
  const getEventById = (eventId: string) => {
    return allEvents.find(event => event.id === eventId);
  };
  const getChannelById = (channelId: string) => {
    return channels.find(channel => channel.id === channelId);
  };
  const getFrequencyLabel = (freq: string) => {
    switch (freq) {
      case 'immediate':
        return 'Immediate';
      case 'daily':
        return 'Daily Digest';
      case 'weekly':
        return 'Weekly Digest';
      default:
        return freq;
    }
  };
  const handleToggleRule = (ruleId: string) => {
    setRules(rules => rules.map(rule => rule.id === ruleId ? {
      ...rule,
      isEnabled: !rule.isEnabled
    } : rule));
  };
  const handleEditRule = (rule: NotificationRule) => {
    setEditingRule(rule);
    setSelectedEventIds([rule.eventId]);
    setSelectedChannelIds(rule.channels);
    setFrequency(rule.frequency);
    setRuleScope(rule.scope);
    setIsDrawerOpen(true);
  };
  const handleDeleteRule = (ruleId: string) => {
    setRules(rules => rules.filter(rule => rule.id !== ruleId));
  };
  const handleAddRule = () => {
    setEditingRule(null);
    setSelectedEventIds([]);
    setSelectedChannelIds([]);
    setFrequency('immediate');
    setRuleScope('Global');
    setSelectedOrgUnit(null);
    setIsDrawerOpen(true);
  };
  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setEditingRule(null);
    setSelectedOrgUnit(null);
  };
  const handleOrgUnitChange = (orgUnitId: string, orgUnit: OrgUnit | null) => {
    setSelectedOrgUnit(orgUnit);
  };
  const handleSaveRule = () => {
    if (selectedEventIds.length === 0 || selectedChannelIds.length === 0) {
      return;
    }
    if (ruleScope === 'OrgUnit' && !selectedOrgUnit) {
      return;
    }
    const newRules = selectedEventIds.map(eventId => {
      const event = getEventById(eventId);
      if (!event) return null;
      const ruleData: NotificationRule = {
        id: editingRule?.id || `rule-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        eventId,
        name: `${event.name} Notifications`,
        description: `Send notifications for ${event.name.toLowerCase()}`,
        channels: selectedChannelIds,
        frequency,
        isEnabled: true,
        filters: {},
        scope: ruleScope,
        ...(ruleScope === 'OrgUnit' && selectedOrgUnit ? {
          orgUnitId: selectedOrgUnit.id,
          orgUnitName: selectedOrgUnit.name,
          orgUnitPath: selectedOrgUnit.path
        } : {}),
        createdAt: editingRule?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      return ruleData;
    }).filter(Boolean) as NotificationRule[];
    if (editingRule) {
      setRules(rules => rules.map(rule => rule.id === editingRule.id ? newRules[0] : rule));
    } else {
      setRules(rules => [...rules, ...newRules]);
    }
    setIsDrawerOpen(false);
  };
  const renderRulesList = (rules: NotificationRule[], showOrgUnit: boolean = false) => {
    return <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-19673">
        <ul className="divide-y divide-gray-200" data-id="element-19674">
          {rules.length === 0 ? <li className="px-6 py-12 text-center" data-id="element-19675">
              <p className="text-sm text-gray-500" data-id="element-19676">
                No notification rules configured.
              </p>
            </li> : rules.map(rule => {
          const event = getEventById(rule.eventId);
          return <li key={rule.id} data-id="element-19677">
                  <div className="px-4 py-4 sm:px-6" data-id="element-19678">
                    {showOrgUnit && rule.orgUnitName && <div className="mb-2" data-id="element-19679">
                        <p className="text-xs font-medium text-gray-500" data-id="element-19680">
                          {rule.orgUnitName}
                        </p>
                        <p className="text-xs text-gray-400" data-id="element-19681">
                          {rule.orgUnitPath}
                        </p>
                      </div>}
                    <div className="flex items-center justify-between" data-id="element-19682">
                      <div className="flex items-center" data-id="element-19683">
                        <button onClick={() => handleToggleRule(rule.id)} className={`mr-3 h-5 w-5 rounded-full flex items-center justify-center ${rule.isEnabled ? 'text-green-500 bg-green-50' : 'text-gray-400 bg-gray-100'}`} data-id="element-19684">
                          {rule.isEnabled ? <CheckCircleIcon className="h-5 w-5" data-id="element-19685" /> : <XCircleIcon className="h-5 w-5" data-id="element-19686" />}
                        </button>
                        <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-19687">
                          {rule.name}
                        </p>
                      </div>
                      <div className="ml-2 flex-shrink-0 flex" data-id="element-19688">
                        <StatusBadge status={rule.isEnabled ? 'success' : 'error'} text={rule.isEnabled ? 'Enabled' : 'Disabled'} data-id="element-19689" />
                      </div>
                    </div>
                    <div className="mt-2 sm:flex sm:justify-between" data-id="element-19690">
                      <div className="sm:flex" data-id="element-19691">
                        <p className="flex items-center text-sm text-gray-500" data-id="element-19692">
                          Event: {event?.name || 'Unknown Event'}
                        </p>
                        <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6" data-id="element-19693">
                          <ClockIcon className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" aria-hidden="true" data-id="element-19694" />
                          {getFrequencyLabel(rule.frequency)}
                        </p>
                      </div>
                      <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0" data-id="element-19695">
                        <p data-id="element-19696">
                          Channels:{' '}
                          {rule.channels.map(channelId => getChannelById(channelId)?.name || 'Unknown').join(', ')}
                        </p>
                      </div>
                    </div>
                    <div className="mt-2 flex justify-end space-x-2" data-id="element-19697">
                      <button type="button" onClick={() => handleEditRule(rule)} className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19698">
                        <EditIcon className="h-4 w-4" aria-hidden="true" data-id="element-19699" />
                      </button>
                      <button type="button" onClick={() => handleDeleteRule(rule.id)} className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" data-id="element-19700">
                        <TrashIcon className="h-4 w-4" aria-hidden="true" data-id="element-19701" />
                      </button>
                    </div>
                  </div>
                </li>;
        })}
        </ul>
      </div>;
  };
  return <div className="space-y-8" data-id="element-19702">
      {/* Global Rules */}
      <div data-id="element-19703">
        <div className="mb-6 flex justify-between items-center" data-id="element-19704">
          <div data-id="element-19705">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-19706">
              Global Notification Rules
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-19707">
              Global rules and events available to all organisation units
            </p>
          </div>
          <button type="button" onClick={handleAddRule} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19708">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-19709" />
            Add Rule
          </button>
        </div>
        {renderRulesList(globalRules, false)}
      </div>

      {/* OrgUnit Rules */}
      <div data-id="element-19710">
        <div className="mb-6" data-id="element-19711">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-19712">
            Organisation Unit Rules
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-19713">
            Rules and events specific to individual organisation units
          </p>
        </div>
        {renderRulesList(orgUnitRules, true)}
      </div>

      {/* Rule Configuration Drawer */}
      {isDrawerOpen && <div className="fixed inset-0 overflow-hidden z-50" data-id="element-19714">
          <div className="absolute inset-0 overflow-hidden" data-id="element-19715">
            <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseDrawer} data-id="element-19716"></div>
            <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex" data-id="element-19717">
              <div className="relative w-screen max-w-md" data-id="element-19718">
                <div className="h-full flex flex-col bg-white shadow-xl overflow-y-scroll" data-id="element-19719">
                  <div className="px-4 py-6 sm:px-6" data-id="element-19720">
                    <div className="flex items-start justify-between" data-id="element-19721">
                      <h2 className="text-lg font-medium text-gray-900" data-id="element-19722">
                        {editingRule ? 'Edit Rule' : 'Create New Rule'}
                      </h2>
                      <div className="ml-3 h-7 flex items-center" data-id="element-19723">
                        <button type="button" className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500" onClick={handleCloseDrawer} data-id="element-19724">
                          <span className="sr-only" data-id="element-19725">Close panel</span>
                          <XIcon className="h-6 w-6" aria-hidden="true" data-id="element-19726" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 py-6 overflow-y-auto px-4 sm:px-6" data-id="element-19727">
                    <div className="space-y-6" data-id="element-19728">
                      {/* Scope Selector */}
                      <div data-id="element-19729">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-19730">
                          Scope <span className="text-red-500" data-id="element-19731">*</span>
                        </label>
                        <select value={ruleScope} onChange={e => setRuleScope(e.target.value as NotificationScope)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-19732">
                          <option value="Global" data-id="element-19733">Global</option>
                          <option value="OrgUnit" data-id="element-19734">Organisation Unit</option>
                        </select>
                        <p className="mt-1 text-xs text-gray-500" data-id="element-19735">
                          {ruleScope === 'Global' ? 'Available to all organisation units' : 'Specific to selected organisation unit'}
                        </p>
                      </div>

                      {/* OrgUnit Selector */}
                      {ruleScope === 'OrgUnit' && <div data-id="element-19736">
                          <OrgUnitSelector value={selectedOrgUnit?.id || ''} onChange={handleOrgUnitChange} label="Organisation Unit" placeholder="Select organisation unit" required={true} showPath={true} data-id="element-19737" />
                        </div>}

                      {/* Event Selection */}
                      <div data-id="element-19738">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-19739">
                          Select Events
                        </label>
                        <div className="mt-2 space-y-2" data-id="element-19740">
                          {allEvents.filter(event => event.scope === ruleScope || ruleScope === 'OrgUnit' && event.scope === 'OrgUnit' && event.orgUnitId === selectedOrgUnit?.id).map(event => <div key={event.id} className="relative flex items-start" data-id="element-19741">
                                <div className="flex items-center h-5" data-id="element-19742">
                                  <input id={`event-${event.id}`} type="checkbox" checked={selectedEventIds.includes(event.id)} onChange={e => {
                            if (e.target.checked) {
                              setSelectedEventIds([...selectedEventIds, event.id]);
                            } else {
                              setSelectedEventIds(selectedEventIds.filter(id => id !== event.id));
                            }
                          }} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-19743" />
                                </div>
                                <div className="ml-3 text-sm" data-id="element-19744">
                                  <label htmlFor={`event-${event.id}`} className="font-medium text-gray-700" data-id="element-19745">
                                    {event.name}
                                  </label>
                                  <p className="text-gray-500" data-id="element-19746">
                                    {event.description}
                                  </p>
                                </div>
                              </div>)}
                        </div>
                      </div>

                      {/* Channel Selection */}
                      <div data-id="element-19747">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-19748">
                          Delivery Channels
                        </label>
                        <div className="mt-2 space-y-2" data-id="element-19749">
                          {channels.filter(channel => channel.isConfigured && (channel.scope === 'Global' || ruleScope === 'OrgUnit' && channel.scope === 'OrgUnit' && channel.orgUnitId === selectedOrgUnit?.id)).map(channel => <div key={channel.id} className="relative flex items-start" data-id="element-19750">
                                <div className="flex items-center h-5" data-id="element-19751">
                                  <input id={`channel-${channel.id}`} type="checkbox" checked={selectedChannelIds.includes(channel.id)} onChange={e => {
                            if (e.target.checked) {
                              setSelectedChannelIds([...selectedChannelIds, channel.id]);
                            } else {
                              setSelectedChannelIds(selectedChannelIds.filter(id => id !== channel.id));
                            }
                          }} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-19752" />
                                </div>
                                <div className="ml-3 text-sm" data-id="element-19753">
                                  <label htmlFor={`channel-${channel.id}`} className="font-medium text-gray-700" data-id="element-19754">
                                    {channel.name}
                                  </label>
                                </div>
                              </div>)}
                        </div>
                      </div>

                      {/* Frequency Selection */}
                      <div data-id="element-19755">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-19756">
                          Notification Frequency
                        </label>
                        <div className="mt-2 space-y-2" data-id="element-19757">
                          <div className="relative flex items-start" data-id="element-19758">
                            <div className="flex items-center h-5" data-id="element-19759">
                              <input id="frequency-immediate" type="radio" checked={frequency === 'immediate'} onChange={() => setFrequency('immediate')} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-19760" />
                            </div>
                            <div className="ml-3 text-sm" data-id="element-19761">
                              <label htmlFor="frequency-immediate" className="font-medium text-gray-700" data-id="element-19762">
                                Immediate
                              </label>
                              <p className="text-gray-500" data-id="element-19763">
                                Send notifications as events occur
                              </p>
                            </div>
                          </div>
                          <div className="relative flex items-start" data-id="element-19764">
                            <div className="flex items-center h-5" data-id="element-19765">
                              <input id="frequency-daily" type="radio" checked={frequency === 'daily'} onChange={() => setFrequency('daily')} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-19766" />
                            </div>
                            <div className="ml-3 text-sm" data-id="element-19767">
                              <label htmlFor="frequency-daily" className="font-medium text-gray-700" data-id="element-19768">
                                Daily Digest
                              </label>
                              <p className="text-gray-500" data-id="element-19769">
                                Group notifications and send once per day
                              </p>
                            </div>
                          </div>
                          <div className="relative flex items-start" data-id="element-19770">
                            <div className="flex items-center h-5" data-id="element-19771">
                              <input id="frequency-weekly" type="radio" checked={frequency === 'weekly'} onChange={() => setFrequency('weekly')} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-19772" />
                            </div>
                            <div className="ml-3 text-sm" data-id="element-19773">
                              <label htmlFor="frequency-weekly" className="font-medium text-gray-700" data-id="element-19774">
                                Weekly Digest
                              </label>
                              <p className="text-gray-500" data-id="element-19775">
                                Group notifications and send once per week
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Optional Filters */}
                      <div data-id="element-19776">
                        <div className="flex items-center justify-between" data-id="element-19777">
                          <label className="block text-sm font-medium text-gray-700" data-id="element-19778">
                            Additional Filters (Optional)
                          </label>
                          <button type="button" className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19779">
                            <FilterIcon className="h-4 w-4 mr-1" data-id="element-19780" />
                            Add Filter
                          </button>
                        </div>
                        <div className="mt-2" data-id="element-19781">
                          <p className="text-sm text-gray-500" data-id="element-19782">
                            No filters applied. Notifications will be sent for
                            all matching events.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex-shrink-0 px-4 py-4 flex justify-end border-t border-gray-200" data-id="element-19783">
                    <button type="button" className="mr-3 bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onClick={handleCloseDrawer} data-id="element-19784">
                      Cancel
                    </button>
                    <button type="button" className="bg-indigo-600 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onClick={handleSaveRule} data-id="element-19785">
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}